import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useI18n } from '../i18n/I18nContext';
import Seo from '../components/Seo';
import getApiBase from '../utils/apiBase';
import { MAX_FORUM_IMAGES, MAX_FORUM_IMAGE_BYTES, MAX_FORUM_SOURCE_BYTES, MAX_FORUM_IMAGE_PIXELS, prepareForumImage } from '../utils/forumImages';
import './CommunityForum.css';

const API = getApiBase();
async function api(path, method = 'GET', body) {
  const response = await fetch(`${API}/api/community/${path}`, {
    method, credentials: 'include',
    ...(body instanceof FormData ? { body } : body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const keys = { 400: 'invalid', 401: 'auth', 403: 'forbidden', 404: 'notFound', 409: 'conflict', 413: 'large', 429: 'rate' };
    const imageErrors = { invalid_image: 'invalidImage', image_too_large: 'imageTooLarge', too_many_images: 'tooManyImages', image_dimensions: 'imageDimensions' };
    const error = new Error();
    error.translationKey = 'forum.error.' + (imageErrors[data.code] || keys[response.status] || 'generic');
    throw error;
  }
  return data;
}

function PhotoPreview({ file, alt }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const preview = URL.createObjectURL(file);
    setUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [file]);
  return url ? <img src={url} alt={alt} /> : null;
}

export default function CommunityForum() {
  const { t, formatDate, formatNumber } = useI18n();
  const date = value => formatDate(value, { month: 'short', day: 'numeric', year: 'numeric' });
  const { articleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [next, setNext] = useState(null);
  const [commentNext, setCommentNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [writing, setWriting] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [preparingImages, setPreparingImages] = useState(false);
  const imageInput = useRef(null);
  const imageOperation = useRef(0);
  const preparingRef = useRef(false);
  const [comment, setComment] = useState('');
  const [revision, setRevision] = useState(0);
  const path = articleId ? `/community/articles/${articleId}` : '/community';

  useEffect(() => {
    setImages([]); setPreparingImages(false); preparingRef.current = false;
    return () => { imageOperation.current += 1; };
  }, [articleId, user]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setArticle(null); setComments([]); setWriting(false); setComment('');
    const load = async () => {
      try {
        if (articleId) {
          const [post, replies] = await Promise.all([api(`articles/${articleId}`), api(`articles/${articleId}/comments`)]);
          if (active) { setArticle(post); setComments(replies.items); setCommentNext(replies.next_offset); }
        } else {
          const feed = await api('articles');
          if (active) { setItems(feed.items); setNext(feed.next_offset); }
        }
      } catch (e) { if (active) setError(e.translationKey || 'forum.error.generic'); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [articleId, user, revision]);

  const run = async (action) => {
    if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try { await action(); } catch (e) { setError(e.translationKey || 'forum.error.generic'); }
    finally { setBusy(false); }
  };
  const requireLogin = () => {
    if (user) return true;
    navigate(`/login?next=${encodeURIComponent(path)}`);
    return false;
  };
  const publish = (event) => {
    event.preventDefault();
    if (preparingRef.current || !requireLogin()) return;
    run(async () => {
      let body = { title, content };
      if (images.length) {
        body = new FormData();
        body.append('title', title);
        body.append('content', content);
        images.forEach(image => body.append('images', image.file));
      }
      const result = await api('articles', 'POST', body);
      setTitle(''); setContent(''); setImages([]); setWriting(false);
      navigate(`/community/articles/${result.id}`);
    });
  };
  const addImages = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || busy || preparingRef.current) return;
    setError('');
    if (images.length + files.length > MAX_FORUM_IMAGES) {
      setError('forum.error.tooManyImages');
      return;
    }
    const operation = imageOperation.current;
    preparingRef.current = true; setPreparingImages(true);
    try {
      const prepared = [];
      for (const file of files) {
        prepared.push({ file: await prepareForumImage(file), name: file.name });
        if (operation !== imageOperation.current) return;
      }
      setImages(current => [...current, ...prepared]);
    } catch (e) {
      if (operation === imageOperation.current) setError(e.translationKey || 'forum.error.imageCompression');
    } finally {
      if (operation === imageOperation.current) {
        preparingRef.current = false; setPreparingImages(false);
      }
    }
  };
  const report = (type, id) => {
    const reasons = ['spam', 'harassment', 'inappropriate', 'other'];
    const choice = window.prompt(t('forum.reportPrompt', {
      reasons: reasons.map((reason, index) => `${index + 1}. ${t('community.report.' + reason)}`).join('\n'),
    }), '1');
    if (choice === null) return;
    const reason = reasons[['1', '2', '3', '4'].indexOf(choice.trim())];
    if (!reason) { setError('forum.error.invalid'); return; }
    run(async () => { await api('reports', 'POST', { target_type: type, target_id: id, reason }); setNotice('community.report.submitted'); });
  };
  const share = () => run(async () => {
    const url = `${window.location.origin}/community/articles/${article.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: article.title, url }); }
      catch (e) { if (e.name !== 'AbortError') throw e; }
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url); setNotice('forum.copied');
    } else { window.prompt(t('forum.copyPrompt'), url); }
  });

  return (
    <main className="forum-page">
      <Seo title={article?.title || t('forum.seoTitle')} description={article ? article.content.slice(0, 160) : t('forum.seoDescription')} path={path} noindex={Boolean(articleId)} />
      <header className="forum-hero"><span>{t('forum.eyebrow')}</span><h1>{articleId ? t('forum.detailHero') : t('forum.hero')}</h1><p>{t('forum.intro')}</p>
        {!articleId && <button onClick={() => { if (requireLogin()) setWriting(!writing); }}>{writing ? t('forum.close') : t('forum.write')}</button>}
      </header>
      {error && <div className="forum-message" role="alert">{t(error, {
        count: formatNumber(MAX_FORUM_IMAGES),
        limit: formatNumber(error === 'forum.error.imageSourceLarge' ? MAX_FORUM_SOURCE_BYTES / 1024 / 1024 : error === 'forum.error.imageDimensions' ? MAX_FORUM_IMAGE_PIXELS / 1000000 : MAX_FORUM_IMAGE_BYTES / 1024),
      })} <button disabled={busy || preparingImages} onClick={() => setRevision(revision + 1)}>{t('forum.retry')}</button></div>}
      {notice && <p className="forum-message" role="status">{t(notice)}</p>}
      {writing && <form className="forum-editor" onSubmit={publish}><h2>{t('forum.editorTitle')}</h2><p>{t('forum.rules')}</p><label htmlFor="article-title">{t('forum.title')}</label><input id="article-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={160} required disabled={busy} /><label htmlFor="article-body">{t('forum.body')}</label><textarea id="article-body" rows={12} value={content} onChange={e => setContent(e.target.value)} maxLength={20000} placeholder={t('forum.placeholder')} required disabled={busy} /><small>{t('forum.characters', { count: formatNumber(content.length), limit: formatNumber(20000) })}</small>
        <fieldset className="forum-image-picker" disabled={busy || preparingImages}>
          <legend>{t('forum.images')}</legend>
          <input ref={imageInput} id="article-images" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden aria-label={t('forum.addImages')} aria-describedby="article-image-hint" onChange={addImages} disabled={images.length >= MAX_FORUM_IMAGES} />
          <button type="button" disabled={images.length >= MAX_FORUM_IMAGES} onClick={() => imageInput.current?.click()}>{t('forum.addImages')}</button>
          <small id="article-image-hint">{t('forum.imageHint', { count: formatNumber(MAX_FORUM_IMAGES), sourceLimit: formatNumber(MAX_FORUM_SOURCE_BYTES / 1024 / 1024), limit: formatNumber(MAX_FORUM_IMAGE_BYTES / 1024) })}</small>
          {!!images.length && <div className="forum-image-previews">{images.map((image, index) => <figure key={index}>
            <PhotoPreview file={image.file} alt={t('forum.imageAlt', { number: formatNumber(index + 1) })} />
            <figcaption><span>{image.name}</span><span>{t('forum.imageSize', { size: formatNumber(Math.ceil(image.file.size / 1024)) })}</span></figcaption>
            <button type="button" onClick={() => setImages(current => current.filter((_, position) => position !== index))}>{t('forum.removeImage', { number: formatNumber(index + 1) })}</button>
          </figure>)}</div>}
        </fieldset>
        {preparingImages && <p role="status">{t('forum.preparingImages')}</p>}
        <button disabled={busy || preparingImages || !title.trim() || !content.trim()}>{busy ? t('forum.publishing') : t('forum.publish')}</button></form>}
      {loading ? <p role="status">{t('forum.loading')}</p> : articleId ? article && <>
        <Link className="forum-back" to="/community">{t('forum.all')}</Link>
        <article className="forum-article"><p className="forum-meta">{article.author_name || t('forum.member')} · {date(article.created_at)}</p><h2>{article.title}</h2><div className="forum-body">{article.content}</div>
          {!!article.images?.length && <div className="forum-article-images">{article.images.map((image, index) => <img key={image.id} src={`${API}${image.url}`} width={image.width} height={image.height} loading="lazy" alt={t('forum.imageAlt', { number: formatNumber(index + 1) })} />)}</div>}
          <div className="forum-actions">
          <button disabled={busy} aria-pressed={article.liked} onClick={() => { if (requireLogin()) run(async () => { await api(`articles/${article.id}/like`, article.liked ? 'DELETE' : 'PUT'); setArticle(await api(`articles/${article.id}`)); }); }}>{article.liked ? t('forum.liked') : t('forum.like')} · {formatNumber(article.like_count)}</button>
          <a href="#article-comments">{t('forum.comments', { count: formatNumber(article.comment_count) })}</a><button disabled={busy} onClick={share}>{t('forum.share')}</button>
          {article.is_owner ? <button disabled={busy} onClick={() => { if (window.confirm(t('forum.deleteArticle'))) run(async () => { await api(`articles/${article.id}`, 'DELETE'); navigate('/community'); }); }}>{t('community.actions.delete')}</button> : user && <button disabled={busy} onClick={() => report('article', article.id)}>{t('community.actions.report')}</button>}
        </div></article>
        <section className="forum-comments" id="article-comments"><h2>{t('forum.conversation')}</h2>
          {user ? <form onSubmit={event => { event.preventDefault(); run(async () => { await api(`articles/${article.id}/comments`, 'POST', { content: comment }); setComment(''); setRevision(revision + 1); }); }}><label htmlFor="forum-comment">{t('forum.addComment')}</label><textarea id="forum-comment" value={comment} onChange={e => setComment(e.target.value)} maxLength={1000} rows={3} required /><button disabled={busy || !comment.trim()}>{t('forum.postComment')}</button></form> : <p><Link to={`/login?next=${encodeURIComponent(path)}`}>{t('forum.login')}</Link></p>}
          {!comments.length && <p>{t('forum.noComments')}</p>}
          {comments.map(reply => <article className="forum-comment" key={reply.id}><p className="forum-meta">{reply.author_name || t('forum.member')} · {date(reply.created_at)}</p><p className="forum-body">{reply.content}</p>{reply.is_owner ? <button disabled={busy} onClick={() => { if (window.confirm(t('forum.deleteComment'))) run(async () => { await api(`articles/${article.id}/comments/${reply.id}`, 'DELETE'); setRevision(revision + 1); }); }}>{t('community.actions.delete')}</button> : user && <button disabled={busy} onClick={() => report('comment', reply.id)}>{t('community.actions.report')}</button>}</article>)}
          {commentNext !== null && <button disabled={busy} onClick={() => run(async () => { const data = await api(`articles/${article.id}/comments?offset=${commentNext}`); setComments([...comments, ...data.items]); setCommentNext(data.next_offset); })}>{t('forum.moreComments')}</button>}
        </section>
      </> : <section className="forum-feed"><div className="forum-feed-heading"><h2>{t('forum.latest')}</h2><span>{t('forum.byPlayers')}</span></div>
        {!items.length && <div className="forum-empty"><h3>{t('forum.emptyTitle')}</h3><p>{t('forum.emptyBody')}</p></div>}
        {items.map(item => <article className="forum-card" key={item.id}><p className="forum-meta">{item.author_name || t('forum.member')} · {date(item.created_at)}</p><h3><Link to={`/community/articles/${item.id}`}>{item.title}</Link></h3><p>{item.content}{item.content.length === 240 ? '…' : ''}</p>
          {item.images?.[0] && <Link className="forum-card-image" to={`/community/articles/${item.id}`}><img src={`${API}${item.images[0].url}`} width={item.images[0].width} height={item.images[0].height} loading="lazy" alt={t('forum.imageAlt', { number: formatNumber(1) })} /></Link>}
          <div className="forum-meta">{t('forum.stats', { likes: formatNumber(item.like_count), comments: formatNumber(item.comment_count) })} <Link to={`/community/articles/${item.id}`}>{t('forum.read')}</Link></div></article>)}
        {next !== null && <button disabled={busy} onClick={() => run(async () => { const data = await api(`articles?offset=${next}`); setItems([...items, ...data.items]); setNext(data.next_offset); })}>{t('forum.more')}</button>}
      </section>}
    </main>
  );
}
